import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Pagination } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
} from "recharts";
import { Activity, Package, CircleDollarSign, XCircle, Percent, Sparkles, Tag, Loader2, ArrowRight, ImageOff, ClipboardCheck, Check } from "lucide-react";

const C = { green: "#16a34a", red: "#ef4444", orange: "#f97316", sky: "#0284c7", amber: "#f59e0b", slate: "#94a3b8", violet: "#7c3aed" };

function Kpi({ icon: Icon, label, value, sub, accent, testid, onClick }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`rounded-xl bg-white p-4 text-left ring-1 ring-inset ring-slate-200 transition-all ${onClick ? "hover:-translate-y-0.5 hover:shadow-md hover:ring-brand-green/40" : "cursor-default"}`}>
      <div className="flex items-center justify-between">
        <p className="overline">{label}</p>
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${accent}`}><Icon className="h-4 w-4 text-white" /></div>
      </div>
      <p className="tabular mt-2 font-heading text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
    </button>
  );
}

function ChartCard({ title, caption, children, testid }) {
  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid={testid}>
      <p className="font-heading text-sm font-bold text-slate-800">{title}</p>
      {caption && <p className="mb-3 mt-0.5 text-[11px] text-slate-500">{caption}</p>}
      {children}
    </div>
  );
}

function Donut({ data, colors, unit = "" }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width={150} height={150}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2} stroke="none">
            {data.map((d, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${v}${unit}`, n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-1.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colors[i % colors.length] }} /> {d.name}
            </span>
            <span className="tabular font-semibold text-slate-800">{d.value}<span className="ml-1 text-[10px] text-slate-400">{total ? `%${Math.round(d.value / total * 100)}` : ""}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MissingImagesCard({ total }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [count, setCount] = useState(total || 0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);

  const fetchPage = async (p, sz, query) => {
    const _sz = sz ?? size;
    const _p = p ?? page;
    const _q = query ?? q;
    setLoading(true);
    try {
      const skip = (_p - 1) * _sz;
      const r = await api.get(`/analytics/missing-images?limit=${_sz}&skip=${skip}${_q ? `&q=${encodeURIComponent(_q)}` : ""}`);
      setCount(r.data.total || 0);
      setItems(r.data.items || []);
      setPage(_p);
    } catch { /* yoksay */ }
    finally { setLoading(false); }
  };

  const toggle = () => { const n = !open; setOpen(n); if (n && items.length === 0) fetchPage(1); };
  const onPageSize = (s) => { setSize(s); fetchPage(1, s); };

  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="ch-missing-images-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-fuchsia-600 text-white"><ImageOff className="h-4 w-4" /></div>
          <div>
            <p className="font-heading text-sm font-bold text-slate-800">Görseli Eksik Ürünler</p>
            <p className="text-[11px] text-slate-500"><b className="tabular text-fuchsia-700">{count}</b> ürünün görseli yok — AI Görseller panelinden toplu üretebilirsiniz.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/urunler")} data-testid="ch-missing-goto-products"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            Ürünlere git <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button onClick={toggle} data-testid="ch-missing-toggle"
            className="rounded-md bg-fuchsia-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-fuchsia-700">
            {open ? "Gizle" : "Listeyi Gör"}
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4" data-testid="ch-missing-list">
          <div className="mb-2 flex gap-2">
            <input data-testid="ch-missing-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchPage(1, size, q)} placeholder="Ürün adı / SKU ara…"
              className="h-9 flex-1 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400" />
            <button onClick={() => fetchPage(1, size, q)} className="rounded-md bg-slate-100 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-200">Ara</button>
          </div>
          {items.length === 0 && !loading ? (
            <p className="py-4 text-center text-sm text-slate-400">Görseli eksik ürün bulunamadı.</p>
          ) : (
            <>
              <div className="rounded-lg ring-1 ring-inset ring-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                    <tr><th className="px-3 py-2">Ürün</th><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Kategori</th></tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={it.id} data-testid={`ch-missing-row-${i}`} className="border-t border-slate-50">
                        <td className="px-3 py-1.5 font-medium text-slate-700">{it.name}</td>
                        <td className="px-3 py-1.5 tabular text-slate-500">{it.sku}</td>
                        <td className="px-3 py-1.5 text-slate-500">{it.category || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3">
                <Pagination page={page} pageSize={size} total={count} onPage={(p) => fetchPage(p)}
                  onPageSize={onPageSize} sizes={[20, 50, 100]} testid="ch-missing-pagination" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NamingAuditCard() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState({});
  const UNITS = ["Lt", "Ml", "Gr", "Kg", "Adet", "Cc", "Oz", "M", "Cm", "Paket"];

  const load = async () => {
    setLoading(true);
    api.get("/products/brands").then((r) => setBrands(r.data.brands || [])).catch(() => {});
    try {
      const r = await api.get("/products/naming-audit?limit=300");
      setData(r.data);
      const init = {};
      (r.data.items || []).forEach((it) => { init[it.id] = { brand: "", no_brand: false, quantity: "", unit: "", saving: false, done: false, newName: "" }; });
      setRows(init);
    } catch { toast.error("Denetim yüklenemedi"); }
    finally { setLoading(false); }
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !data) load(); };
  const patch = (id, p) => setRows((r) => ({ ...r, [id]: { ...r[id], ...p } }));
  const complete = async (it) => {
    const row = rows[it.id] || {};
    if (it.missing.includes("brand") && !row.no_brand && !(row.brand || "").trim()) return toast.error("Marka girin veya Markasız seçin");
    if (it.missing.includes("measure") && !((row.quantity || "").trim() && row.unit)) return toast.error("Miktar ve birim girin");
    patch(it.id, { saving: true });
    try {
      const { data: res } = await api.post(`/products/${it.id}/naming-complete`, { brand: row.brand, no_brand: row.no_brand, quantity: row.quantity, unit: row.unit });
      patch(it.id, { saving: false, done: true, newName: res.name });
      toast.success("Güncellendi: " + res.name);
    } catch (e) { patch(it.id, { saving: false }); toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
  };
  const items = data?.items || [];
  const pending = items.filter((it) => !rows[it.id]?.done);
  const [sel, setSel] = useState({});
  const [brands, setBrands] = useState([]);
  const [bulkBrand, setBulkBrand] = useState("");
  const toggleSel = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const selCount = Object.values(sel).filter(Boolean).length;
  const bulkNoBrand = async () => {
    const ids = Object.keys(sel).filter((id) => sel[id]);
    if (!ids.length) return toast.error("Önce ürün seçin");
    try {
      const { data: res } = await api.post("/products/bulk-no-brand", { ids });
      toast.success(`${res.updated} ürün 'Markasız' yapıldı`);
      setSel({}); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
  };
  const bulkSetBrand = async () => {
    const ids = Object.keys(sel).filter((id) => sel[id]);
    if (!ids.length) return toast.error("Önce ürün seçin");
    const b = (bulkBrand || "").trim();
    if (!b) return toast.error("Marka girin");
    try {
      const { data: res } = await api.post("/products/bulk-set-brand", { ids, brand: b });
      toast.success(`${res.matched} ürüne '${b}' markası atandı`);
      setSel({}); setBulkBrand(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="ch-naming-audit-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-indigo-600 text-white"><ClipboardCheck className="h-4 w-4" /></div>
          <div>
            <p className="font-heading text-sm font-bold text-slate-800">Adlandırma Denetimi — Marka / Miktar / Birim</p>
            <p className="text-[11px] text-slate-500">Adı <b>"Ürün Adı Marka Miktar Birim"</b> kuralına uymayan ürünleri tek ekranda tamamlayın.{data ? <> <b className="text-indigo-700">{data.missing_brand}</b> marka eksik · <b className="text-indigo-700">{data.missing_measure}</b> miktar/birim eksik</> : ""}</p>
          </div>
        </div>
        <button onClick={toggle} data-testid="ch-naming-audit-toggle" className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
          {open ? "Gizle" : "Denetle & Tamamla"}
        </button>
      </div>
      {open && (
        <div className="mt-4" data-testid="ch-naming-audit-list">
          {loading ? (
            <div className="flex justify-center py-6 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : pending.length === 0 ? (
            <p className="py-4 text-center text-sm text-emerald-600" data-testid="ch-naming-audit-empty">Tüm ürün adları kurala uygun 🎉</p>
          ) : (
            <>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button data-testid="ch-naming-bulk-nobrand" onClick={bulkNoBrand} disabled={selCount === 0}
                className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40">
                Seçilenleri Markasız Yap ({selCount})
              </button>
              <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:block" />
              <input list="ch-naming-brand-list" data-testid="ch-naming-bulk-brand-input" value={bulkBrand}
                onChange={(e) => setBulkBrand(e.target.value)} placeholder="Marka (örn. Groot)"
                className="h-8 w-44 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green" />
              <datalist id="ch-naming-brand-list">{brands.map((b) => <option key={b} value={b} />)}</datalist>
              <button data-testid="ch-naming-bulk-setbrand" onClick={bulkSetBrand} disabled={selCount === 0 || !bulkBrand.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-brand-green px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-40">
                <Tag className="h-3.5 w-3.5" /> Seçilenlere Marka Ata ({selCount})
              </button>
              <span className="text-[11px] text-slate-400">Aynı markaya ait ürünleri seçip tek tıkla marka atayın; ad otomatik kurala uydurulur.</span>
            </div>
            <div className="overflow-x-auto rounded-lg ring-1 ring-inset ring-slate-100">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="px-3 py-2">Ürün</th><th className="px-3 py-2">Eksik</th>
                    <th className="px-3 py-2">Marka</th><th className="px-3 py-2">Miktar</th>
                    <th className="px-3 py-2">Birim</th><th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const row = rows[it.id] || {};
                    if (row.done) return (
                      <tr key={it.id} data-testid={`ch-naming-row-${it.id}`} className="border-t border-slate-50 bg-emerald-50/40">
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 font-medium text-slate-700">{row.newName || it.name}</td>
                        <td className="px-3 py-2 text-emerald-600" colSpan={5}><Check className="mr-1 inline h-3.5 w-3.5" /> Tamamlandı</td>
                      </tr>
                    );
                    return (
                      <tr key={it.id} data-testid={`ch-naming-row-${it.id}`} className="border-t border-slate-50 align-top">
                        <td className="px-3 py-2"><input type="checkbox" data-testid={`ch-naming-sel-${it.id}`} checked={!!sel[it.id]} onChange={() => toggleSel(it.id)} className="h-3.5 w-3.5" /></td>
                        <td className="px-3 py-2 font-medium text-slate-700">{it.name}<br /><span className="tabular text-[10px] text-slate-400">{it.sku}</span></td>
                        <td className="px-3 py-2">
                          {it.missing.includes("brand") && <span className="mr-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Marka</span>}
                          {it.missing.includes("measure") && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Miktar</span>}
                        </td>
                        <td className="px-3 py-2">
                          {it.missing.includes("brand") ? (
                            <div className="flex items-center gap-1.5">
                              <input data-testid={`ch-naming-brand-${it.id}`} value={row.brand || ""} disabled={row.no_brand}
                                onChange={(e) => patch(it.id, { brand: e.target.value })} placeholder="Marka"
                                className="h-8 w-28 rounded-md border border-slate-200 px-2 text-xs disabled:bg-slate-100" />
                              <label className="flex items-center gap-1 text-[10px] text-slate-500">
                                <input type="checkbox" data-testid={`ch-naming-nobrand-${it.id}`} checked={!!row.no_brand}
                                  onChange={(e) => patch(it.id, { no_brand: e.target.checked })} className="h-3.5 w-3.5" /> Markasız
                              </label>
                            </div>
                          ) : <span className="text-[11px] text-slate-400">{it.brand || "—"}</span>}
                        </td>
                        <td className="px-3 py-2">
                          {it.missing.includes("measure")
                            ? <input data-testid={`ch-naming-qty-${it.id}`} value={row.quantity || ""} onChange={(e) => patch(it.id, { quantity: e.target.value })} placeholder="1" className="h-8 w-16 rounded-md border border-slate-200 px-2 text-xs tabular" />
                            : <span className="text-[11px] text-slate-400">✓</span>}
                        </td>
                        <td className="px-3 py-2">
                          {it.missing.includes("measure")
                            ? <select data-testid={`ch-naming-unit-${it.id}`} value={row.unit || ""} onChange={(e) => patch(it.id, { unit: e.target.value })} className="h-8 rounded-md border border-slate-200 px-1 text-xs">
                                <option value="">—</option>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                              </select>
                            : <span className="text-[11px] text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button data-testid={`ch-naming-save-${it.id}`} onClick={() => complete(it)} disabled={row.saving}
                            className="rounded-md bg-brand-green px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-50">
                            {row.saving ? "…" : "Tamamla"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
          {data && data.total_incomplete > items.length && <p className="mt-2 text-[11px] text-slate-400">İlk {items.length} ürün gösteriliyor (toplam {data.total_incomplete}); bu grup bitince yeniden açın.</p>}
        </div>
      )}
    </div>
  );
}

export default function CatalogHealth() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(() => {
    const request = ++requestRef.current;
    setLoadError(false);
    setD(null);
    return api.get("/analytics/catalog-health").then((r) => {
      if (request === requestRef.current) setD(r.data);
    }).catch(() => {
      if (request === requestRef.current) setLoadError(true);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loadError) return (
    <div role="alert" data-testid="catalog-health-error" className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
      <h1 className="font-heading text-2xl font-bold">Katalog Sağlığı</h1>
      <p>Katalog denetimi yüklenemedi. Bağlantıyı kontrol edip yeniden deneyin.</p>
      <Button onClick={load} variant="outline">Yeniden Dene</Button>
    </div>
  );

  if (!d) return <div className="flex h-64 items-center justify-center text-slate-400" data-testid="catalog-health-loading"><Loader2 className="h-7 w-7 animate-spin" /></div>;

  const pricing = [{ name: "Fiyatlı", value: d.priced }, { name: "Fiyatsız", value: d.unpriced }];
  const kdv = [{ name: "Tamam", value: d.kdv.ok }, { name: "Doğrula bekliyor", value: d.kdv.pending }];
  const rates = (d.kdv.by_rate || []).map((r) => ({ name: `KDV %${r.rate}`, value: r.count }));
  const membership = [
    { name: "Sadece Market", value: d.channel_membership.market_only },
    { name: "Her iki kanal", value: d.channel_membership.both },
    { name: "Sadece Toptan", value: d.channel_membership.toptan_only },
  ];
  const chData = (d.channels || []).map((c) => ({ name: c.label, priced: c.priced, unpriced: d.total - c.priced }));
  const catData = (d.top_unpriced_categories || []).map((c) => ({ name: c.category, value: c.unpriced }));

  return (
    <div className="space-y-6 fade-in" data-testid="catalog-health-page">
      <div className="flex items-center gap-2.5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-brand-green"><Activity className="h-5 w-5" /></div>
        <div>
          <p className="overline">Katalog & Fiyat</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Katalog Sağlığı</h1>
        </div>
      </div>

      <p className="rounded-md bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700 ring-1 ring-inset ring-emerald-200">
        Birleşik katalog tek bakışta: kaç ürün fiyatlı/fiyatsız, KDV doğrulaması ne durumda, hangi kategoriler eksik. Kartlara tıklayarak ilgili ekrana geçin.
      </p>

      {/* KPI kartları */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi testid="ch-kpi-total" icon={Package} label="Toplam Ürün" value={d.total} sub="Kanonik kart" accent="bg-slate-700" onClick={() => nav("/urunler")} />
        <Kpi testid="ch-kpi-priced" icon={CircleDollarSign} label="Fiyatlı" value={d.priced} sub={`%${d.priced_pct}`} accent="bg-brand-green" />
        <Kpi testid="ch-kpi-unpriced" icon={XCircle} label="Fiyatsız" value={d.unpriced} sub="Fiyatlanmayı bekliyor" accent="bg-red-500" onClick={() => nav("/toplu-fiyat")} />
        <Kpi testid="ch-kpi-kdv" icon={Percent} label="KDV Bekleyen" value={d.kdv.pending} sub={`%${d.kdv.verified_pct} tamam`} accent="bg-amber-500" onClick={() => nav("/kdv-dogrulama")} />
        <Kpi testid="ch-kpi-benchmark" icon={Sparkles} label="Piyasa Benchmarkı" value={d.benchmarks} sub="Doğrulanmış gözlem" accent="bg-sky-600" onClick={() => nav("/firsatlar")} />
        <Kpi testid="ch-kpi-alias" icon={Tag} label="Alias / Eski SKU" value={d.aliases} sub={`${d.fresh_locks} taze görsel kilitli`} accent="bg-violet-600" />
        <Kpi testid="ch-kpi-missing-images" icon={ImageOff} label="Görseli Eksik" value={d.missing_images ?? 0} sub="AI ile üretilebilir" accent="bg-fuchsia-600" onClick={() => nav("/urunler")} />
      </div>

      {/* Görseli eksik ürünler */}
      <MissingImagesCard total={d.missing_images ?? 0} />

      {/* Adlandırma denetimi — Marka / Miktar / Birim toplu tamamlama */}
      <NamingAuditCard />

      {/* Donutlar */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ChartCard testid="ch-pricing-donut" title="Fiyatlama Durumu" caption={`${d.total} üründen %${d.priced_pct}'i fiyatlı — ${d.unpriced} ürün hâlâ fiyatsız.`}>
          <Donut data={pricing} colors={[C.green, C.red]} />
        </ChartCard>
        <ChartCard testid="ch-kdv-donut" title="KDV Doğrulama" caption={`${d.kdv.pending} ürün manuel KDV kontrolü bekliyor (%${d.kdv.verified_pct} tamam).`}>
          <Donut data={kdv} colors={[C.green, C.amber]} />
        </ChartCard>
        <ChartCard testid="ch-membership-donut" title="Kanal Üyeliği" caption="Her ürün tek karttır; kaç ürün hem market hem toptan?">
          <Donut data={membership} colors={[C.orange, C.violet, C.sky]} />
        </ChartCard>
      </div>

      {/* KDV oran dağılımı + Kanal kapsamı */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard testid="ch-rate-donut" title="KDV Oran Dağılımı" caption="Ürünlerin KDV oranlarına göre dağılımı (yalnız %1 / %10 / %20).">
          <Donut data={rates} colors={[C.green, C.sky, C.orange]} />
        </ChartCard>
        <ChartCard testid="ch-channel-bar" title="Kanal Fiyat Kapsamı" caption="Her satış kanalında kaç ürün fiyatlı? (Yeşil = fiyatlı)">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
              <Tooltip />
              <Bar dataKey="priced" name="Fiyatlı" fill={C.green} radius={[0, 4, 4, 0]}>
                <LabelList dataKey="priced" position="right" style={{ fontSize: 11, fill: "#475569" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* En çok fiyatsız kategoriler */}
      <ChartCard testid="ch-topcat-bar" title="En Çok Fiyatsız Kategoriler" caption="Fiyatlamaya buradan başlamak en yüksek etkiyi verir. Toplu Fiyatlandırma'da kategori seçerek hızlıca kapatın.">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={catData} layout="vertical" margin={{ left: 40, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
            <Tooltip formatter={(v) => [`${v} fiyatsız`, "Ürün"]} />
            <Bar dataKey="value" name="Fiyatsız ürün" fill={C.orange} radius={[0, 4, 4, 0]} cursor="pointer"
              onClick={() => nav("/toplu-fiyat")}>
              <LabelList dataKey="value" position="right" style={{ fontSize: 11, fill: "#475569" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <button onClick={() => nav("/toplu-fiyat")} data-testid="ch-goto-pricing"
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline">
          Toplu Fiyatlandırma'ya git <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </ChartCard>
    </div>
  );
}
